/**
 * Project view shell (§7g Commit E).
 *
 * Loads the current project from the store, renders the header +
 * tab nav, and mounts the tab content via react-router's Outlet.
 * The nested routes are declared in App.jsx; this component is the
 * layout their content renders inside.
 *
 * Loading state: if the projects list hasn't been loaded yet (direct
 * deep-link from a bookmark), we fire loadProject which falls back to
 * a single-project fetch via the API.
 *
 * Not found / archived: if loadProject throws a 404 we navigate back
 * to the project list. No dedicated "not found" page — the list is a
 * better recovery target than a standalone error screen.
 */

import { useEffect } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header.jsx";
import ProjectHeader from "../components/ProjectHeader.jsx";
import ProjectSidebar from "../components/ProjectSidebar.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";
import SetupIncompleteBanner from "../components/SetupIncompleteBanner.jsx";
import { useStore } from "../store/useStore.js";

export default function ProjectView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const currentProject = useStore((s) => s.currentProject);
  const loadProject = useStore((s) => s.loadProject);
  const resetProjectScope = useStore((s) => s.resetProjectScope);
  const documents = useStore((s) => s.documents);
  const conversations = useStore((s) => s.conversations);
  const jobs = useStore((s) => s.jobs);

  useEffect(() => {
    let cancelled = false;
    loadProject(projectId).catch(() => {
      if (!cancelled) navigate("/", { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, loadProject, navigate]);

  // Defensive cleanup on true unmount (user navigates OUT of the
  // project subtree entirely — to /coaches, /, /login etc).
  //
  // Note: this is a belt-and-braces measure. The fix commit for the
  // 7g code-review blockers moved synchronous clears into the store
  // loaders themselves (loadProject, loadDocuments, loadConversations),
  // which handles project-switch transitions within the /projects/*
  // subtree. This cleanup additionally covers the case where a future
  // consumer on a non-project route reads `currentProject`,
  // `documents`, or `conversations` — without it, those slices would
  // retain stale values from the last-viewed project. Cheap enough to
  // keep. Uses the consolidated `resetProjectScope` action (G9 /
  // JAM-144) so this call site, the archive path, and any future
  // delete-project flow stay aligned.
  useEffect(() => {
    return () => {
      resetProjectScope();
    };
  }, [resetProjectScope]);

  const sections = [
    { to: "overview", label: "Overview", count: null },
    { to: "documents", label: "Documents", count: documents.length || null },
    {
      to: "conversations",
      label: "Conversations",
      count: conversations.length || null,
    },
    { to: "jobs", label: "Jobs", count: jobs.length || null },
  ];

  return (
    <>
      <Header />
      <main className="project-view">
        {currentProject ? (
          <>
            <ProjectSidebar sections={sections} />
            <div className="project-content">
              <ProjectHeader project={currentProject} />
              <SetupIncompleteBanner />
              <Outlet />
            </div>
          </>
        ) : (
          <div className="project-view-loading">
            <LoadingSkeleton variant="line" size="sm" />
            <LoadingSkeleton variant="line" />
            <LoadingSkeleton variant="line" size="short" />
          </div>
        )}
      </main>
    </>
  );
}
